import React from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import KindergartenSwitcher from './components/KindergartenSwitcher';
import FinanceTrendChart from './components/FinanceTrendChart';
import MonthStepper from '../../components/MonthStepper';
import TodoList from './components/Overview/TodoList';
import MonthFigures from './components/Overview/MonthFigures';
import DebtDonut from './components/Overview/DebtDonut';
import CompositionDonut from './components/Overview/CompositionDonut';
import NextMonthCard from './components/Overview/NextMonthCard';
import LocationsTable from './components/Overview/LocationsTable';
import ExportMenu from './components/Overview/ExportMenu';
import { forMonth } from './components/Overview/monthText';
import SectionTitle from './components/Overview/SectionTitle';
import { useFinanceKindergarten } from './useFinanceKindergarten';
import { useFinanceMonth } from './useFinanceMonth';
import { useFinanceSummary } from './useFinanceSummary';
import { FINANCE } from '../../common/constants/routes.constants';
import { FeatureCode } from '../../common/enums/common.enum';
import { useKindergartenFeatures } from '../../utilities/hooks/useKindergartenFeatures';
import { useFinanceComposition, useFinanceToday } from './useFinanceToday';

/**
 * What the Finance section opens on: what needs doing, the month in figures, where the
 * money is owed, what the month was invoiced for and what next month looks like, then the
 * kindergarten by location.
 *
 * "To do", today's debts and next month are about today; the figures, the composition and
 * the locations are about the month chosen. A location's and a group's pages are laid out
 * the same way, over their own scope.
 */
const FinanceDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { kindergartenId, kindergartenName } = useFinanceKindergarten();
  const [month, setMonth] = useFinanceMonth();
  const { isEnabled, isLoading: isLoadingFeatures } = useKindergartenFeatures(kindergartenId || undefined);
  const enabled = !!kindergartenId && !isLoadingFeatures && isEnabled(FeatureCode.INVOICING);
  const { summary, trend, isLoading, failed } = useFinanceSummary({
    kindergartenId: enabled ? kindergartenId : '',
    month,
    trendMonths: 12
  });
  const { todo, buckets, nextMonth } = useFinanceToday({ kindergartenId, enabled });
  const composition = useFinanceComposition({ kindergartenId, enabled, month });

  const body = () => {
    if (!kindergartenId) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {t('finance.pickKindergarten')}
        </Alert>
      );
    }
    if (isLoadingFeatures || (isLoading && !summary)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!isEnabled(FeatureCode.INVOICING)) {
      return (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t('finance.invoicingOff')}
        </Alert>
      );
    }
    if (failed || !summary) {
      return (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {t('finance.loadFailed')}
        </Alert>
      );
    }
    const monthLabel = forMonth(t, month);
    return (
      <>
        {todo && <TodoList items={todo} kindergartenId={kindergartenId} />}

        <Box component="section" aria-labelledby="finance-month-title" sx={{ mb: 3 }}>
          <SectionTitle
            id="finance-month-title"
            aside={
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, justifyContent: 'space-between' }}>
                <MonthStepper value={month} onChange={setMonth} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('balances.amountsInRsd')}</Typography>
              </Stack>
            }
          >
            {t('finance.overview.month')}
          </SectionTitle>
          <MonthFigures
            month={month}
            totals={summary.totals}
            points={trend?.points ?? []}
            buckets={buckets}
            kindergartenId={kindergartenId}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3, mb: 3 }}>
          {trend ? <FinanceTrendChart trend={trend} onSelectMonth={setMonth} selectedMonth={month} /> : <Box />}
          <DebtDonut buckets={buckets} kindergartenId={kindergartenId} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 3 }}>
          <CompositionDonut composition={composition} monthLabel={monthLabel} />
          <NextMonthCard data={nextMonth} kindergartenId={kindergartenId} />
        </Box>

        <Box component="section" aria-labelledby="finance-locations-title">
          <SectionTitle id="finance-locations-title">{t('finance.overview.byLocation', { month: monthLabel })}</SectionTitle>
          <LocationsTable
            rows={summary.breakdown}
            totals={summary.totals}
            onRowClick={row => navigate(`${FINANCE}/location/${row.id}?kindergartenId=${kindergartenId}&month=${month}`)}
          />
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ p: 7 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h2">{t('finance.overview.title')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t('finance.pages.overviewDescription')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {enabled && <ExportMenu kindergartenId={kindergartenId} kindergartenName={kindergartenName ?? ''} month={month} />}
          {/* switching kindergarten keeps the month, so two kindergartens compare like for like */}
          <KindergartenSwitcher kindergartenId={kindergartenId} href={id => `${FINANCE}?kindergartenId=${id}&month=${month}`} />
        </Stack>
      </Stack>
      {body()}
    </Box>
  );
};

export default FinanceDashboard;
